import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { EstateTag } from '../EstateTag'
import { WaDatePicker } from '../WaDatePicker'
import { WaTimePicker } from '../WaTimePicker'
import type { PlanHuntTeamOption } from './types'

/** The line under a team's name in the picker: its member count and, for an estate, the estate tag. */
function TeamMetaLine({ members, isEstate, fontSize }: { members?: number; isEstate?: boolean; fontSize: number }) {
  const { t } = useTranslation('planHunt')
  if (members == null && !isEstate) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
      {members != null && (
        <span style={{ fontSize, color: COLORS.outline }}>{t('basics.membersCount', { count: members })}</span>
      )}
      {isEstate && <EstateTag />}
    </div>
  )
}

export interface PlanHuntBasicsState {
  groupId: string | null
  huntName: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
}

export interface PlanHuntBasicsStepProps {
  teams: PlanHuntTeamOption[]
  state: PlanHuntBasicsState
  teamLocked?: boolean
  maxNameLength?: number
  onChange: (next: PlanHuntBasicsState) => void
}

const fmtDay = (d: Date) => {
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' })
  const month = d.toLocaleDateString('en-US', { month: 'short' })
  return `${weekday}, ${d.getDate()} ${month} ${d.getFullYear()}`
}

/**
 * Live, human-readable summary of the planned schedule. A hunt confined to a
 * single day collapses to one date with a time range; a hunt that spans days
 * shows both full datetimes. Returns null until every field is filled.
 */
export function previewHuntSchedule(s: PlanHuntBasicsState): string | null {
  if (!s.startDate || !s.startTime || !s.endDate || !s.endTime) return null
  const start = new Date(`${s.startDate}T${s.startTime}:00`)
  const end = new Date(`${s.endDate}T${s.endTime}:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  if (s.startDate === s.endDate) {
    return `${fmtDay(start)}, ${s.startTime}–${s.endTime}`
  }
  return `${fmtDay(start)} ${s.startTime} → ${fmtDay(end)} ${s.endTime}`
}

/**
 * Whether the chosen end is not strictly after the chosen start, comparing the
 * full start/end datetimes (so an overnight hunt that ends at an earlier clock
 * time on the next day is valid). False while the range is incomplete.
 */
export function isEndBeforeStart(s: PlanHuntBasicsState): boolean {
  if (!s.startDate || !s.startTime || !s.endDate || !s.endTime) return false
  return new Date(`${s.startDate}T${s.startTime}:00`) >= new Date(`${s.endDate}T${s.endTime}:00`)
}

const pad2 = (n: number) => String(n).padStart(2, '0')
const localDateStr = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

/**
 * Default start time when a start date is picked:
 *   - a future day → 08:00
 *   - today → now + 2h, rounded to the nearest 5 minutes, capped at 23:59 so a
 *     late-evening "today" never rolls onto the next day.
 * `now` is injected so the rule is testable without faking the clock.
 */
export function defaultStartTime(startDate: string, now: Date): string {
  if (startDate !== localDateStr(now)) return '08:00'
  const plus2h = new Date(now.getTime() + 2 * 60 * 60 * 1000)
  if (localDateStr(plus2h) !== localDateStr(now)) return '23:59' // +2h crossed midnight
  const mins = Math.round((plus2h.getHours() * 60 + plus2h.getMinutes()) / 5) * 5
  if (mins > 23 * 60 + 59) return '23:59' // rounding pushed past end of day
  return `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`
}

/**
 * What the start time should become when the start date changes. Re-applies the
 * date-driven default ONLY when the user hasn't manually edited the time, so an
 * explicit choice is never silently overwritten.
 */
export function startTimeForDateChange(
  s: PlanHuntBasicsState,
  newStartDate: string,
  startTimeEdited: boolean,
  now: Date,
): string {
  return startTimeEdited ? s.startTime : defaultStartTime(newStartDate, now)
}

const labelStyle: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  fontSize: 12, fontWeight: 600, color: 'var(--nl-outline)',
  textTransform: 'uppercase', letterSpacing: 0.3,
}

function TeamBadge({ name, size = 36 }: { name: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 8, flexShrink: 0,
      background: COLORS.primary, color: COLORS.onPrimary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size <= 28 ? 11 : 12, fontWeight: 700,
    }}>
      {name.slice(0, 2).toUpperCase()}
    </div>
  )
}

/**
 * The team picker: one field that reads as the chosen team, opening a list of
 * the teams you could pick instead.
 *
 * It replaced a grid of team cards with an Expand/Collapse link above it. The
 * grid put every team on screen at once, which reads as the step's subject
 * rather than as one of its five fields — and it grew a row for every team a
 * hunter belongs to, pushing the date and time fields below the fold for
 * anyone in more than a handful. A closed field is the same height whether you
 * are in two teams or twenty.
 */
function TeamDropdown({
  teams,
  selectedId,
  locked,
  onSelect,
}: {
  teams: PlanHuntTeamOption[]
  selectedId: string | null
  /** Planning from a team's own page fixes the team — the field still reads as
      the chosen team, but there is nothing to open. */
  locked?: boolean
  onSelect: (id: string) => void
}) {
  const { t } = useTranslation('planHunt')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const selected = teams.find((team) => team.id === selectedId)

  // Closing on an outside click is what makes the panel safe to leave open:
  // it overlays the fields below it, so without this the only way past it
  // would be to pick a team you did not want.
  useEffect(() => {
    if (!open) return
    const onDocPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDocPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const fieldStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 12,
    width: '100%', minHeight: 56, padding: '8px 16px',
    border: `1.5px solid ${open ? COLORS.primary : COLORS.surfaceContainerHighest}`,
    borderRadius: 8,
    background: COLORS.surfaceContainerLowest,
    textAlign: 'left',
    cursor: locked ? 'default' : 'pointer',
  }

  const face = selected ? (
    <>
      <TeamBadge name={selected.name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{selected.name}</div>
        <TeamMetaLine members={selected.members} isEstate={selected.isEstate} fontSize={12} />
      </div>
    </>
  ) : (
    <>
      <Icon name="group" size={22} color={COLORS.outline} />
      <span style={{ flex: 1, fontSize: 14, color: COLORS.outline }}>{t('basics.selectTeam')}</span>
    </>
  )

  if (locked) {
    return <div style={{ ...fieldStyle, background: COLORS.surface }}>{face}</div>
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        // The select-only combobox pattern: a button that owns a listbox. The
        // step's date and time fields are also buttons that expand, so the role
        // is what tells this one apart for assistive tech and for tests.
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        style={fieldStyle}
      >
        {face}
        <Icon
          name="chevron-down"
          size={20}
          color={COLORS.outline}
          style={{ transform: open ? 'rotate(180deg)' : undefined, flexShrink: 0 }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 20,
            // Tall enough for a handful of teams, scrolling past that rather
            // than running off the step.
            maxHeight: 320,
            overflowY: 'auto',
            background: COLORS.surfaceContainerLowest,
            border: `1.5px solid ${COLORS.surfaceContainerHighest}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
          }}
        >
          {teams.map((team, i) => {
            const active = team.id === selectedId
            return (
              <button
                key={team.id}
                type="button"
                role="option"
                aria-selected={active}
                data-selected={active}
                onClick={() => {
                  onSelect(team.id)
                  setOpen(false)
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', padding: '12px 16px',
                  background: active ? COLORS.surfaceContainer : 'transparent',
                  border: 'none',
                  borderTop: i === 0 ? 'none' : `1px solid ${COLORS.outlineVariant}`,
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                }}
              >
                <TeamBadge name={team.name} size={32} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    data-selected={active}
                    style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {team.name}
                  </div>
                  <TeamMetaLine members={team.members} isEstate={team.isEstate} fontSize={12} />
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function PlanHuntBasicsStep({
  teams, state, teamLocked = false, maxNameLength = 120, onChange,
}: PlanHuntBasicsStepProps) {
  const { t } = useTranslation('planHunt')
  const today = new Date().toISOString().slice(0, 10)
  const preview = useMemo(() => previewHuntSchedule(state), [state])
  const endBeforeStart = isEndBeforeStart(state)
  // Tracks whether the user has typed a start time, so changing the start date
  // re-applies the default only when they haven't picked their own time.
  const [startTimeEdited, setStartTimeEdited] = useState(false)

  const handleStartDate = (v: string) => {
    onChange({ ...state, startDate: v, startTime: startTimeForDateChange(state, v, startTimeEdited, new Date()) })
  }
  const handleStartTime = (v: string) => {
    setStartTimeEdited(true)
    onChange({ ...state, startTime: v })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 620, width: '100%', margin: '0 auto' }}>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        {/* Team legend row with REQUIRED badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <legend style={{ fontSize: 12, fontWeight: 600, color: COLORS.outline, textTransform: 'uppercase', letterSpacing: 0.3, padding: 0, float: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            {t('basics.team')}
            <span style={{ color: COLORS.error, fontWeight: 700 }}>*</span>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
              padding: '2px 6px', borderRadius: 4,
              background: COLORS.errorContainer ?? '#fde8e8',
              color: COLORS.onErrorContainer ?? COLORS.error,
              textTransform: 'uppercase',
            }}>
              {t('basics.required')}
            </span>
          </legend>
        </div>

        <TeamDropdown
          teams={teams}
          selectedId={state.groupId}
          locked={teamLocked}
          onSelect={(id) => onChange({ ...state, groupId: id })}
        />
      </fieldset>

      <label style={labelStyle}>
        {t('basics.huntName')}
        <input
          className="wh-input"
          type="text"
          maxLength={maxNameLength}
          placeholder={t('basics.huntNamePlaceholder')}
          value={state.huntName}
          onChange={(e) => onChange({ ...state, huntName: e.target.value })}
        />
        <span data-testid="name-counter" style={{ fontSize: 11, color: state.huntName.length >= maxNameLength ? COLORS.error : COLORS.outline }}>
          {state.huntName.length} / {maxNameLength}
        </span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
        <div style={labelStyle}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('basics.startDate')}
            <span style={{ color: COLORS.error, fontWeight: 700 }}>*</span>
          </span>
          <WaDatePicker
            value={state.startDate}
            min={today}
            onChange={handleStartDate}
          />
        </div>
        <div style={labelStyle}>
          {t('basics.startTime')}
          <WaTimePicker
            value={state.startTime}
            onChange={handleStartTime}
          />
        </div>
        <div style={labelStyle}>
          {t('basics.endDate')}
          <WaDatePicker
            value={state.endDate}
            min={state.startDate || today}
            onChange={(v) => onChange({ ...state, endDate: v })}
          />
        </div>
        <div style={labelStyle}>
          {t('basics.endTime')}
          <WaTimePicker
            value={state.endTime}
            onChange={(v) => onChange({ ...state, endTime: v })}
          />
        </div>
      </div>

      {!state.startDate && (
        <p style={{ color: COLORS.error, margin: 0, fontSize: 12 }}>{t('basics.startDateRequired')}</p>
      )}

      {endBeforeStart && (
        <p role="alert" style={{ color: COLORS.error, margin: 0 }}>
          {t('basics.endBeforeStart', { startTime: state.startTime, endTime: state.endTime })}
        </p>
      )}

      {preview && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, background: COLORS.primaryContainer,
          border: `1px solid ${COLORS.primaryFixedDim}`, display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 13, color: COLORS.primary,
        }}>
          <Icon name="calendar" size={16} />
          <span data-testid="basics-preview">{preview}</span>
        </div>
      )}
    </div>
  )
}
